import "./App.css";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import ReservationCountdown from "@/global/components/ReservationCountdown/ReservationCountdown";
import AppSnackbarHost from "@/global/components/AppSnackbar/AppSnackbar";
import { refreshReservationAndMenu } from "@/lib/reservationBootstrap";
import CenteredLoader from "@/global/components/Loader/CenteredLoader";
import { useNavigate } from "react-router-dom";
import { store } from "@/store/store";
import { setEditingOrder } from "./features/ui/uiSlice";

const PreorderHome = lazy(() => import("./screens/PreOrderHome/PreorderHome"));
const Menu = lazy(() => import("./screens/Menu/Menu"));
const SingleItemPage = lazy(
  () => import("./screens/Menu/components/SingleItemPage/SingleItemPage"),
);
const CartPage = lazy(() => import("./screens/Cart/CartPage"));
const ReservationLoader = lazy(
  () => import("@/screens/PreOrderHome/ReservationLoader"),
);
const NoReservation = lazy(
  () => import("@/screens/NoReservation/NoReservation"),
);

function App() {
  const reservation = useAppSelector((s) => s.reservation);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const lastFetchedIdRef = useRef<string | undefined>(undefined);
  const [bootstrapping, setBootstrapping] = useState(false);

  // If we navigate away from the edit context (order-confirmed or item editor),
  // automatically disable edit mode so cart items don't show as editable.
  useEffect(() => {
    const isOnEditItem = location.pathname.startsWith("/menu/item/");
    const isOnPreorderHome = location.pathname === "/order-confirmed";
    if (!isOnEditItem && !isOnPreorderHome) {
      // Turn off edit mode when leaving edit/confirm screens
      if (store.getState().ui?.isEditingOrder) {
        dispatch(setEditingOrder(false));
      }
    }
  }, [location.pathname, dispatch]);

  const isExpired = Boolean(
    reservation.expiresAtMs && reservation.expiresAtMs <= Date.now(),
  );
  const hasReservation = Boolean(
    reservation.reservationId && reservation.loaded,
  );
  const hasOrderItems = Boolean(
    Array.isArray((reservation as any)?.order?.items) &&
      (reservation as any).order.items.length > 0,
  );

  // On app mount/refresh, fetch latest reservation + menu if we have an id
  // and we're not currently on the reservation bootstrap route.
  useEffect(() => {
    const isOnReservationBootstrapRoute =
      location.pathname.startsWith("/reservation/");
    const id = reservation.reservationId;
    if (!id || isOnReservationBootstrapRoute) return;

    (async () => {
      try {
        if (lastFetchedIdRef.current === id) return;
        lastFetchedIdRef.current = id;
        setBootstrapping(true);
        await refreshReservationAndMenu(dispatch, String(id));
        // After refresh, decide landing route based on order items
        const state = store.getState();
        const items: any[] = (state as any)?.reservation?.order?.items || [];
        const hasItems = Array.isArray(items) && items.length > 0;
        const target = hasItems ? "/order-confirmed" : "/menu";
        if (location.pathname !== target) {
          navigate(target, { replace: true });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh reservation on app load:", e);
      }
      setBootstrapping(false);
    })();
    // Only run on mount and when reservationId changes; ignore route changes to avoid double fetching post-bootstrap
  }, [dispatch, reservation.reservationId, location.pathname]);

  return (
    <>
      <ReservationCountdown />
      <AppSnackbarHost />
      {bootstrapping ? (
        <CenteredLoader />
      ) : (
        <Suspense fallback={<CenteredLoader />}>
          {hasReservation ? (
            <Routes>
              <Route
                path="/reservation/:reservationId"
                element={<ReservationLoader />}
              />
              <Route
                path="/"
                element={
                  <Navigate
                    to={hasOrderItems ? "/order-confirmed" : "/menu"}
                    replace
                  />
                }
              />
              <Route path="/order-confirmed" element={<PreorderHome />} />
              <Route
                path="/menu"
                element={isExpired ? <PreorderHome /> : <Menu />}
              />
              {/* legacy per-section routes now redirect to the single menu page */}
              <Route
                path="/menu/drinks"
                element={<Navigate to="/menu" replace />}
              />
              <Route
                path="/menu/bites"
                element={<Navigate to="/menu" replace />}
              />
              <Route
                path="/menu/item/:itemId"
                element={isExpired ? <PreorderHome /> : <SingleItemPage />}
              />
              <Route
                path="/cart"
                element={isExpired ? <PreorderHome /> : <CartPage />}
              />
            </Routes>
          ) : (
            <Routes>
              <Route
                path="/reservation/:reservationId"
                element={<ReservationLoader />}
              />
              <Route path="*" element={<NoReservation />} />
            </Routes>
          )}
        </Suspense>
      )}
    </>
  );
}

export default App;
